// =============================================================================
// Client-side instrumentation entry (Next.js auto-loads this at app boot).
//
// Initializes the self-hosted error reporter — ships errors to
// /api/log/frontend-error → winston → Logstash → Elasticsearch → ElastAlert
// → Telegram. Implementation lives in src/lib/error-reporter.ts.
//
// Runs inside requestIdleCallback so init never blocks first paint.
// Demo mode + session-expired short-circuit are handled inside the reporter.
// =============================================================================

import { initErrorReporter, addBreadcrumb } from "@/lib/error-reporter"
import { initNetJournal } from "@/lib/net-journal"
import { isChunkLoadError, recoverFromChunkError } from "@/lib/chunk-recovery"

const initSelfHostedReporter = () => {
  initErrorReporter({
    endpoint: "/api/log/frontend-error",
    release: process.env.NEXT_PUBLIC_RELEASE_SHA || "unknown",
  })
}

// Chunk-load failures that never reach a React error boundary — a bare
// `import()` rejected inside an event handler, or a prefetch that failed after
// the component already unmounted. The boundaries handle the rendering path;
// this covers everything else so a stale build cannot leave a tab quietly
// broken with no route to recovery.
//
// All the safety gates live in recoverFromChunkError: it probes the server
// first and refuses to reload when the device is offline, when an order sheet
// is open, or when this build already spent its one automatic reload.
const installChunkRecovery = () => {
  if (typeof window === "undefined") return

  window.addEventListener("unhandledrejection", (ev) => {
    if (!isChunkLoadError(ev.reason)) return
    void recoverFromChunkError(ev.reason)
  })

  window.addEventListener("error", (ev) => {
    if (!isChunkLoadError(ev.error)) return
    void recoverFromChunkError(ev.error)
  })
}

// Capture clicks on intentionally-tagged elements only (allow-list via
// `data-track="<label>"`). Document-level listener with `passive: true` —
// one listener for the whole app, zero per-component instrumentation.
// We walk up at most 3 ancestors so a click on the icon *inside* a tagged
// button still attributes to the button.
const installClickCrumb = () => {
  if (typeof document === "undefined") return
  document.addEventListener(
    "click",
    (ev) => {
      try {
        let el = ev.target as HTMLElement | null
        for (let i = 0; i < 3 && el; i++) {
          const tag = el.dataset?.track
          if (tag) {
            addBreadcrumb("ui", `click [data-track="${tag}"]`, { target: tag })
            return
          }
          el = el.parentElement
        }
      } catch { /* never let crumb capture break interactivity */ }
    },
    { passive: true, capture: true }
  )
}

if (typeof window !== "undefined") {
  // requestIdleCallback isn't in older browsers — fall back to a short
  // setTimeout so the init still runs without blocking the critical path.
  const ric = (
    window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void
    }
  ).requestIdleCallback
  const boot = () => {
    initSelfHostedReporter()
    // Recover a connectivity outage that spanned this page load. Must run
    // AFTER the reporter is initialised, because a confirmed-reachable probe
    // ships the outage summary through it. This is the path that finally
    // delivers an incident the user "escaped" by hitting Try Again — the
    // reload that used to destroy every trace of it.
    initNetJournal()
    installClickCrumb()
    installChunkRecovery()
    // APK fleet inventory (no-op on web/PWA). Dynamic import keeps the
    // module out of the critical path; must run AFTER initSelfHostedReporter
    // since it ships through the reporter.
    import("@/lib/device-inventory")
      .then((m) => m.logDeviceInventory())
      .catch(() => { /* telemetry only — never block boot */ })
  }
  if (typeof ric === "function") {
    ric(boot, { timeout: 2000 })
  } else {
    setTimeout(boot, 0)
  }
}
